import React, { useEffect, useRef, useState } from "react";
import { useExport } from "../../providers/ExportProvider";
import { useLazyGetPurlFileExportJobStatusQuery } from "../../services/api";
import { MdDragIndicator } from "react-icons/md";
import { IoIosArrowDown } from "react-icons/io";
import { showNotification } from "../../services/showNotification";
import styles from "./index.module.css";

const Loader = () => (
  <div
    className="w-[18px] h-[18px] border-2 border-white/20 border-t-white rounded-full animate-spin"
  />
);

export const ExportManager = () => {
  const { jobs, removeJob } = useExport();
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({
    x: window.innerWidth / 2 - 120,
    y: 60,
  });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const [checkJobStatus] = useLazyGetPurlFileExportJobStatusQuery();

  const onMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    dragStart.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    };
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!dragging) return;
    setPos({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const onMouseUp = () => setDragging(false);

  const exportAssetPolling = async (id: string, name: string, folderId?: string) => {
    try {
      const status = await checkJobStatus({ jobId: id }).unwrap();
      
      
      if (status?.data?.isRunning) return;

      
      if (status?.data?.isFailed) {
        removeJob(id);
        showNotification("error", `Export for "${name}" failed. Please try again.`);
        return;
      }

      if (status?.data?.isComplete) {
        removeJob(id);

        if (status.data.downloadUrl) {
          showNotification("success", `"${name}" export ready! Downloading...`);
          window.open(status.data.downloadUrl, "_blank");
        } else {
          showNotification("error", `Failed to retrieve download link for "${name}"`);
        }
      }
    } catch (err) {
      console.error("exportPolling err", err);
      // We don't remove the job immediately on error in case it's a temporary network failure.
      // But maybe we should after some max attempts. For now, keep simple.
    }
  };

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  });

  useEffect(() => {
    if (jobs.length === 0) return;
    
    const interval = setInterval(async () => {
      for (const job of jobs) {
        exportAssetPolling(job.id, job.name, job.folderId);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [jobs, checkJobStatus]); // checkJobStatus is stable, jobs changes when jobs are added/removed

  if (!jobs.length) return null;

  return (
    <div
      ref={panelRef}
      style={{
        left: pos.x,
        top: pos.y,
      }}
      className={`fixed w-[240px] backdrop-blur-[8px] bg-[#3c3c3c]/70 border border-white/15 shadow-[0_20px_40px_rgba(0,0,0,0.4)] text-white px-3 py-1 rounded-[14px] z-[9999] select-none ${dragging ? "cursor-grabbing" : "cursor-default"}`}
    >
      <div className="font-semibold flex justify-between items-center py-2">
        <div className="flex items-center gap-1.5">
          <IoIosArrowDown
            size={16}
            onClick={() => setOpen(!open)}
            className={`cursor-pointer transition-transform duration-250 ease-in-out ${open ? "rotate-180" : "rotate-0"}`}
          />
          <span className="mt-[2px] text-sm">Active Exports ({jobs.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {!open && <Loader />}
          <MdDragIndicator
            onMouseDown={onMouseDown}
            className="cursor-grab"
            size={20}
          />
        </div>
      </div>

      {open && (
        <div className="mt-1">
          {jobs.slice(0, 3).map((job) => (
            <div
              key={job.id}
              className="flex justify-between items-center px-1 py-2.5 border-t border-white/10 text-[13px]"
            >
              <div className="font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[160px]">
                {job.name}
              </div>
              <Loader />
            </div>
          ))}
          {jobs.length > 3 && (
            <div className="text-xs py-2 px-1 text-center border-t border-white/10 opacity-80">
              + {jobs.length - 3} more...
            </div>
          )}
        </div>
      )}
    </div>
  );
};
